import type { TimeseriesDayPoint } from "@/api/campaign";

/** Today's date ("2026-10-14") in the report's timezone (an IANA name such as "UTC"). */
export function todayIn(timezone: string, now: Date = new Date()): string {
	try {
		return new Intl.DateTimeFormat("en-CA", {
			timeZone: timezone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}).format(now);
	} catch {
		return now.toISOString().slice(0, 10);
	}
}

export interface DailyPeriod {
	labels: string[];
	titles: string[];
	views: Array<number | null>;
	engagements: Array<number | null>;
}

/**
 * Chart data for a period (a calendar month or a week) of days, oldest first. Days up to today are
 * plotted, a past day without data as 0 so the line runs along the bottom instead of breaking off.
 * Today without data (its numbers usually aren't in yet) is `null`, so the line stops at yesterday
 * rather than dropping to 0. A period that isn't over yet ends at today, then one blank slot (`null`,
 * no label) leaves a little space at the right edge.
 */
export function dailyPeriod(
	days: string[],
	byDay: Map<string, TimeseriesDayPoint>,
	today: string,
	label: (day: string) => string,
	title: (day: string) => string,
): DailyPeriod {
	const shown = days.filter((day) => day <= today);
	const value = (day: string, metric: "views" | "engagements") => {
		const point = byDay.get(day);
		if (point) return point[metric];
		return day === today ? null : 0;
	};
	const period: DailyPeriod = {
		labels: shown.map(label),
		titles: shown.map(title),
		views: shown.map((day) => value(day, "views")),
		engagements: shown.map((day) => value(day, "engagements")),
	};
	if (shown.length < days.length) {
		period.labels.push("");
		period.titles.push("");
		period.views.push(null);
		period.engagements.push(null);
	}
	return period;
}
